import { apiClient } from './apiClient';
import { apiConfig } from '@/config/apiConfig';
import { handleServiceError } from '@/utils/errorHandler';
import type {
  CascadeLocation,
  CreateCustomLocationResponse,
  LocationSearchParams,
  LocationSearchResponse,
  MasterLocation,
  RefreshCacheResponse,
} from '@/types/consoleLocation';

/**
 * Backoffice location-management service — wraps the endpoints
 * documented in `docs/location-tracking/location-management.md`.
 *
 * Cascade browse returns raw arrays; search returns a paginated
 * envelope. Consumers should reach for `search` for tables and
 * for `getDistricts` / `getSubdistricts` for dependent dropdowns
 * (as recommended in the doc's "Notes for frontend" section).
 */
class ConsoleLocationService {
  private endpoints = apiConfig.endpoints.consoleLocation;

  /**
   * Districts list. Returns a bare `string[]` per the doc's
   * cascade-browse response shape. A 404 (no results) is folded
   * into an empty array so callers don't need to special-case it.
   */
  async getDistricts(): Promise<string[]> {
    try {
      const response = await apiClient.get<string[] | { message: string }>(this.endpoints.base);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (error?.response?.status === 404) return [];
      handleServiceError(error, 'Fetch Districts');
      throw error;
    }
  }

  /** Subdistricts under a district. Empty on 404 (doc's "no results" shape). */
  async getSubdistricts(district: string): Promise<string[]> {
    try {
      const url = `${this.endpoints.base}/${encodeURIComponent(district)}`;
      const response = await apiClient.get<string[] | { message: string }>(url);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (error?.response?.status === 404) return [];
      handleServiceError(error, 'Fetch Subdistricts');
      throw error;
    }
  }

  /**
   * Villages + custom locations under a subdistrict. Rarely needed
   * once the grid uses `search` — the grid pulls the same info with
   * pagination — but kept for pickers that need a full un-paginated
   * list under a specific subdistrict.
   */
  async getVillages(district: string, subdistrict: string): Promise<CascadeLocation[]> {
    try {
      const url = `${this.endpoints.base}/${encodeURIComponent(district)}/${encodeURIComponent(subdistrict)}`;
      const response = await apiClient.get<CascadeLocation[] | { message: string }>(url);
      return Array.isArray(response.data) ? response.data : [];
    } catch (error: any) {
      if (error?.response?.status === 404) return [];
      handleServiceError(error, 'Fetch Villages');
      throw error;
    }
  }

  /**
   * Paginated text search. The doc calls this the preferred entry
   * point for tables and typeahead — search is case-insensitive on
   * `location_name_in_english` and can be combined with district /
   * subdistrict filters. Backend caps `page_size` at 100.
   */
  async search(params: LocationSearchParams = {}): Promise<LocationSearchResponse> {
    try {
      const response = await apiClient.get<LocationSearchResponse>(this.endpoints.search, {
        params,
      });
      return response.data;
    } catch (error: any) {
      // 404 = "No results found." per the doc. Fold into an empty
      // envelope so the grid can render an empty state.
      if (error?.response?.status === 404) {
        return {
          list: [],
          total: 0,
          page: params.page ?? 1,
          page_size: params.page_size ?? 20,
        };
      }
      handleServiceError(error, 'Search Locations');
      throw error;
    }
  }

  /**
   * Per-id lookup. `lgd_code` is the row's `id` (`V_40034` for master
   * rows, `C_<n>` for customs). Returns the full master_location_lookup
   * row — the only endpoint that reliably surfaces latitude / longitude.
   */
  async getById(lgdCode: string): Promise<MasterLocation> {
    try {
      const url = `${this.endpoints.village}/${encodeURIComponent(lgdCode)}`;
      const response = await apiClient.get<MasterLocation>(url);
      return response.data;
    } catch (error: any) {
      handleServiceError(error, 'Fetch Location Details');
      throw error;
    }
  }

  /**
   * Create a custom location under an existing (district, subdistrict).
   * Requires ADMIN. The doc uses path segments (not a JSON body) —
   * matching that here.
   */
  async createCustomLocation(
    district: string,
    subdistrict: string,
    name: string,
  ): Promise<CreateCustomLocationResponse> {
    try {
      const url =
        `${this.endpoints.customLocation}/${encodeURIComponent(district)}` +
        `/${encodeURIComponent(subdistrict)}/${encodeURIComponent(name)}`;
      const response = await apiClient.post<CreateCustomLocationResponse>(url);
      return response.data;
    } catch (error: any) {
      // Preserve the backend's specific 400 messages ("Location data
      // exists" / "Invalid location data") so the form can surface
      // them instead of a generic error toast.
      const backendMessage = error?.response?.data?.error;
      if (backendMessage) throw new Error(backendMessage);
      handleServiceError(error, 'Create Custom Location');
      throw error;
    }
  }

  /** ADMIN cache refresh. Backend rebuild is asynchronous. */
  async refreshCache(): Promise<RefreshCacheResponse> {
    try {
      const response = await apiClient.get<RefreshCacheResponse>(this.endpoints.cache);
      return response.data;
    } catch (error: any) {
      handleServiceError(error, 'Refresh Location Cache');
      throw error;
    }
  }
}

export const consoleLocationService = new ConsoleLocationService();
export default consoleLocationService;
